/**
 * Take Away — κάρτα/IRIS που δεν πέρασε: η ΙΔΙΑ παραγγελία συνεχίζει (αποφάσεις ιδιοκτήτη 12/10/2026).
 *
 * Το ταμείο δεν μαντεύει ποτέ αν χρεώθηκε η κάρτα: σε ΚΑΘΕ αποτέλεσμα που δεν είναι καθαρή έγκριση
 * (απόρριψη, 409, 500, 504 του nginx, σφάλμα δικτύου) ρωτά τον server (POST …/card-check), που ξέρει τι
 * γράφτηκε στη βάση, αν τρέχει ακόμα η χρέωση και — για ό,τι είναι αμφίβολο — τι λέει ο πάροχος.
 *
 *   approved    → ίδια συνέχεια με κανονική έγκριση (απόθεμα, απόδειξη, «Επιτυχής πληρωμή»)
 *   not_charged → τρία κουμπιά στην ίδια παραγγελία: «Ξανά κάρτα», «Μετρητά», «Ακύρωση»
 *   in_flight   → το τερματικό δεν έχει απαντήσει: ξαναρωτάμε μόνοι μας ανά λίγα δευτερόλεπτα
 *   uncertain   → κλείδωμα: «Έλεγχος ξανά» + «κάλεσε τον υπεύθυνο» — ούτε νέα χρέωση ούτε μετρητά
 *   order_cancelled / order_closed → η παραγγελία έκλεισε αλλού· το ταμείο απλώς βγαίνει
 *
 * Τα αιτήματα εδώ δεν πετάνε ποτέ: σφάλμα δικτύου = { ok: false, status: 0 }.
 */
import { API_BASE_URL } from '../components/config/api';

export type CardCheckState = 'approved' | 'not_charged' | 'uncertain' | 'in_flight' | 'order_cancelled' | 'order_closed';

export interface CardCheckResponse {
  state: CardCheckState;
  message?: string;
  reason?: string | null;
  manager_required?: boolean;
  order?: { id: number; shortCode?: string; status?: string; paymentType?: string; total?: number | null } | null;
  /** Μόνο στο approved: ίδιο σχήμα με την απάντηση του /pos/payment. */
  payment?: any;
}

export interface ApiResult<T = any> {
  ok: boolean;
  status: number;
  data: T | null;
}

/** Ανά πόσο ξαναρωτάμε όσο η χρέωση τρέχει ακόμα. */
export const CARD_CHECK_POLL_MS = 4000;
/** ~4 λεπτά: ο πιο αργός πάροχος (Viva 30″ + 190″) με περιθώριο· μετά → «Έλεγχος ξανά» με το χέρι. */
export const CARD_CHECK_MAX_POLLS = 60;

/** Απαντήσεις του server που σημαίνουν «μη χρεώσεις — μάθε πρώτα τι έγινε». */
const CHECK_FIRST_CODES = new Set(['payment_in_progress', 'card_status_uncertain', 'card_already_charged']);
export const mustCheckFirst = (code?: string | null): boolean => !!code && CHECK_FIRST_CODES.has(code);

const KNOWN_STATES: CardCheckState[] = ['approved', 'not_charged', 'uncertain', 'in_flight', 'order_cancelled', 'order_closed'];
export const isCardCheckState = (s: unknown): s is CardCheckState => KNOWN_STATES.includes(s as CardCheckState);

async function postJson<T>(url: string, body?: unknown): Promise<ApiResult<T>> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
      },
      body: JSON.stringify(body ?? {})
    });
    let data: any = null;
    try { data = await res.json(); } catch { data = null; } // 502/504 του nginx: HTML, όχι JSON
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: null };
  }
}

const base = (storeId: number | string, orderId: number) =>
  `${API_BASE_URL}/api/takeaway/${storeId}/orders/${orderId}`;

export const requestCardCheck = (storeId: number | string, orderId: number) =>
  postJson<CardCheckResponse>(`${base(storeId, orderId)}/card-check`);

export const requestCardToCash = (storeId: number | string, orderId: number, expectedTotal: number) =>
  postJson<any>(`${base(storeId, orderId)}/card-to-cash`, { expectedTotal });

export const requestCardCancel = (storeId: number | string, orderId: number) =>
  postJson<any>(`${base(storeId, orderId)}/card-cancel`);

export const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));
